import { apiService } from "@/services";
import { keepPreviousData, useMutation, useQuery, useQueries } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import { BulkRefreshLandscapesParams, BulkRefreshLandscapesResponse, ConsolidatedLandscape, CreateLandscapeParams, CreateLandscapeResponse, GetLandscapesResponse, GetRecentLandscapesResponse, LandscapeItem, LandscapeOutputData } from "@/types";
import { toast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { ApiError } from "@/services/api.service";
import { MAX_SEARCH_PHRASE_LENGTH } from "@/utils/keywordValidation";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import { searchPhraseKey } from "@/utils/searchPhrase";

type DeleteLandscapeMutationContext = {
  snapshots: Array<[readonly unknown[], GetLandscapesResponse | undefined]>;
};

type BulkDeleteLandscapesResponse = {
  message: string;
  deleted: number;
};

export const useGetLandscapes = (heroProductId?: number) => {

  return useQuery<GetLandscapesResponse>({
    queryKey: ['landscapes', heroProductId],
    queryFn: () =>
      apiService.get("/landscapes", {
        hero_product_id: heroProductId,
      }),
    enabled: !!heroProductId,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useGetLandscapeDetail = (landscapeId: string | null) =>
  useQuery<LandscapeOutputData>({
    queryKey: ['landscape-detail', landscapeId],
    queryFn: () => apiService.get(`/landscapes/${landscapeId}`),
    enabled: !!landscapeId,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useCreateLandscape = () =>
  useMutation<CreateLandscapeResponse, unknown, CreateLandscapeParams>({
    mutationFn: (data) => apiService.post("/landscapes", data),
    onSuccess: (data, { hero_asin, search_phrase }) => {
      queryClient.invalidateQueries({ queryKey: ['landscapes', Number(data.landscape.product_id)] });

      toast({
        title: "Landscape Added",
        description: `"${search_phrase.trim()}" landscape start generating for ${hero_asin}.`,
      });
    },
    onError: (error) => handleApiError(error),
  });

export const useDeleteLandscape = () =>
  useMutation<unknown, unknown, { landscapeId: number; heroProductId?: number }, DeleteLandscapeMutationContext>({
    mutationFn: ({ landscapeId }) => apiService.delete(`/landscapes/${landscapeId}`),
    onMutate: async ({ landscapeId, heroProductId }) => {
      const queryKey = ['landscapes', heroProductId];
      await queryClient.cancelQueries({ queryKey });

      const snapshots = queryClient.getQueriesData<GetLandscapesResponse>({ queryKey });

      for (const [key, prev] of snapshots) {
        if (!prev) continue;
        const nextLandscapes: ConsolidatedLandscape[] = (prev.landscapes ?? [])
          .map((cl) => ({
            ...cl,
            instances: (cl.instances ?? []).filter((i) => Number(i.id) !== landscapeId),
          }))
          .filter((cl) => (cl.instances ?? []).length > 0);
        queryClient.setQueryData<GetLandscapesResponse>(key, { landscapes: nextLandscapes });
      }

      return { snapshots };
    },
    onSuccess: (_, { heroProductId }) => {
      queryClient.invalidateQueries({ queryKey: ["product", "detail", heroProductId?.toString()] });

      toast({
        title: "Landscape Deleted",
        description: "Landscape has been deleted successfully.",
      });
    },
    onError: (error, _vars, context) => {
      if (context) {
        for (const [key, snapshot] of context.snapshots) {
          queryClient.setQueryData(key, snapshot);
        }
      }
      handleApiError(error);
    },
  });

export const useBulkDeleteLandscapes = () =>
  useMutation<BulkDeleteLandscapesResponse, unknown, { landscapeIds: number[]; heroProductId?: number }>({
    mutationFn: ({ landscapeIds }) => apiService.delete("/landscapes/bulk-delete", { landscape_ids: landscapeIds }),
    onSuccess: (data, { landscapeIds, heroProductId }) => {
      queryClient.invalidateQueries({ queryKey: ['landscapes', heroProductId] });
      queryClient.invalidateQueries({ queryKey: ["product", "detail", heroProductId?.toString()] });

      const deletedCount = data.deleted;
      const requestedCount = landscapeIds.length;
      const allDeleted = deletedCount === requestedCount;

      toast({
        title: allDeleted ? "Landscapes Deleted" : "Some Landscapes Deleted",
        description: allDeleted
          ? "Selected landscapes have been deleted successfully."
          : `${deletedCount} of ${requestedCount} selected landscapes were deleted. The rest may no longer exist.`,
      });
    },
    onError: (error, { landscapeIds, heroProductId }) => {
      // The server answers 404 when none of the ids is the caller's (gone or foreign alike), so a 404
      // means every selected row was already deleted elsewhere: say nothing, drop them, refetch.
      if (error instanceof ApiError && !error.isNetworkError && error.status === 404) {
        if (heroProductId !== undefined) stripDismissedIds(landscapeIds, heroProductId);
        queryClient.invalidateQueries({ queryKey: ['landscapes', heroProductId] });
        queryClient.invalidateQueries({ queryKey: ["product", "detail", heroProductId?.toString()] });
        return;
      }
      handleApiError(error);
    },
  });

export const useBulkRefreshLandscapes = () =>
  useMutation<BulkRefreshLandscapesResponse, unknown, { landscapes: Array<{ search_phrase: string; hero_asin: string; brand_id: number }>; heroProductId?: number }>({
    mutationFn: ({ landscapes }) => apiService.post("/landscapes/bulk-refresh", { landscapes }),
    onSuccess: (data, { heroProductId }) => {
      queryClient.invalidateQueries({ queryKey: ['landscapes', heroProductId] });

      toast({
        title: "Landscapes Refreshing",
        description: `${data.landscape_ids.length} landscape${data.landscape_ids.length !== 1 ? "s" : ""} started refreshing.`,
      });
    },
    onError: (error) => handleApiError(error),
  });

type DismissGroupVariables = { landscapeIds: number[]; groupKey: string; heroProductId: number };
type DismissGroupContext = { previous: GetLandscapesResponse | undefined };

/** Drops the dismissed ids from the cached list, in case a refetch during the delete put them back. */
const stripDismissedIds = (landscapeIds: number[], heroProductId: number): void => {
  const ids = new Set(landscapeIds);
  queryClient.setQueryData<GetLandscapesResponse>(['landscapes', heroProductId], (current) =>
    current
      ? {
          ...current,
          landscapes: (current.landscapes ?? [])
            .map((g) => ({ ...g, instances: (g.instances ?? []).filter((i) => !ids.has(Number(i.id))) }))
            .filter((g) => g.instances.length > 0),
        }
      : current,
  );
};

/**
 * X on a creating or failed row: removes the whole phrase group optimistically, with no
 * confirmation and no success toast (`components/landscape.md § X on a creating or failed row`).
 */
export const useDismissLandscapeGroup = () =>
  useMutation<unknown, unknown, DismissGroupVariables, DismissGroupContext>({
    mutationKey: ['landscapes', 'dismiss-group'],
    mutationFn: ({ landscapeIds }) => apiService.delete("/landscapes/bulk-delete", { landscape_ids: landscapeIds }),
    onMutate: async ({ groupKey, heroProductId }) => {
      const queryKey = ['landscapes', heroProductId];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<GetLandscapesResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData<GetLandscapesResponse>(queryKey, {
          ...previous,
          landscapes: (previous.landscapes ?? []).filter((g) => searchPhraseKey(g.prioritySearchPhrase) !== groupKey),
        });
      }
      return { previous };
    },
    onError: (error, { landscapeIds, heroProductId }, context) => {
      // The server answers 404 for an id that is gone and for one that is not ours alike (tenancy),
      // so a 404 here means the group is already gone: keep it out, say nothing.
      if (error instanceof ApiError && !error.isNetworkError && error.status === 404) {
        stripDismissedIds(landscapeIds, heroProductId);
        return;
      }
      if (context?.previous) {
        queryClient.setQueryData(['landscapes', heroProductId], context.previous);
      }
      handleApiError(error);
    },
    onSuccess: (_data, { landscapeIds, heroProductId }) => {
      stripDismissedIds(landscapeIds, heroProductId);
    },
    onSettled: (_data, _error, { heroProductId }) => {
      queryClient.invalidateQueries({ queryKey: ['landscapes', heroProductId] });
      queryClient.invalidateQueries({ queryKey: ["product", "detail", String(heroProductId)] });
    },
  });

type RetryLandscapeVariables = {
  searchPhrase: string;
  heroAsin: string;
  brandId: number;
  heroProductId: number;
  groupKey: string;
};

const RETRY_FAILED_MESSAGE = "Could not create the landscape. Please try again.";

const hasServerMessage = (error: ApiError): boolean => {
  const data = error.data;
  if (typeof data !== "object" || data === null) return false;
  const { message, errors } = data as { message?: unknown; errors?: unknown };
  return (typeof message === "string" && message !== "") || (typeof errors === "object" && errors !== null);
};

/**
 * Retry on a failed row: one new landscape through `bulk-refresh`, never the old row's id
 * (`decisions/a-retry-is-a-new-landscape-created-through-bulk-refresh.md`).
 */
export const useRetryLandscape = () =>
  useMutation<BulkRefreshLandscapesResponse, unknown, RetryLandscapeVariables>({
    mutationFn: ({ searchPhrase, heroAsin, brandId }) => {
      const body: BulkRefreshLandscapesParams = {
        landscapes: [{ search_phrase: searchPhrase, hero_asin: heroAsin, brand_id: brandId }],
      };
      return apiService.post("/landscapes/bulk-refresh", body);
    },
    onSuccess: (data, { heroProductId, groupKey }) => {
      const queryKey = ['landscapes', heroProductId];
      const created = data.created?.[0];
      if (created) {
        queryClient.setQueryData<GetLandscapesResponse>(queryKey, (current) => {
          if (!current) return current;
          const groups = current.landscapes ?? [];
          const index = groups.findIndex((g) => searchPhraseKey(g.prioritySearchPhrase) === groupKey);
          if (index === -1) return current;
          const fresh: LandscapeItem = {
            id: String(created.landscape_id),
            status: "queued",
            status_info: "Initializing landscape analysis...",
            date: "",
            top4Hero: 0,
            top4CompetitorAvg: 0,
            page1Hero: 0,
            page1CompetitorAvg: 0,
            keywordCount: 0,
            report_url: null,
            started_at: null,
            ended_at: null,
            state_changed_at: null,
          };
          const target = { ...groups[index], instances: [fresh, ...(groups[index].instances ?? [])] };
          return { ...current, landscapes: [target, ...groups.slice(0, index), ...groups.slice(index + 1)] };
        });
      }
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      if (error instanceof ApiError && !error.isNetworkError && !hasServerMessage(error)) {
        sonnerToast.error(RETRY_FAILED_MESSAGE);
        return;
      }
      handleApiError(error);
    },
  });

export type AddLandscapesVariables = { heroAsin: string; brandId: number; heroProductId: number; phrases: string[] };
export type AddLandscapesResult = { created: string[]; notCreated: string[]; duplicates: string[] };

const quoteJoin = (phrases: string[]): string => phrases.map((p) => `"${p}"`).join(", ");

const codePointLength = (s: string): number => Array.from(s).length;

export const useAddLandscapes = () =>
  useMutation<AddLandscapesResult, unknown, AddLandscapesVariables>({
    mutationFn: async ({ heroAsin, brandId, heroProductId, phrases }) => {
      // Request index -> phrase index, for the phrases short enough to send.
      const sentPhraseIndexes: number[] = [];
      phrases.forEach((phrase, i) => {
        if (codePointLength(phrase) <= MAX_SEARCH_PHRASE_LENGTH) sentPhraseIndexes.push(i);
      });

      if (sentPhraseIndexes.length === 0) {
        return { created: [], notCreated: phrases, duplicates: [] };
      }

      const body: BulkRefreshLandscapesParams = {
        landscapes: sentPhraseIndexes.map((i) => ({
          hero_asin: heroAsin,
          search_phrase: phrases[i],
          brand_id: brandId,
        })),
        refuse_duplicates: true,
        hero_product_id: heroProductId,
      };

      let response: BulkRefreshLandscapesResponse;
      try {
        response = await apiService.post<BulkRefreshLandscapesResponse, BulkRefreshLandscapesParams>(
          "/landscapes/bulk-refresh",
          body,
        );
      } catch (error) {
        // A 4xx answer means nothing was created. A 409 (another request for the product is in
        // flight) falls through to onError so the user reads the server's message. A raw
        // AxiosError (401) and network errors / 5xx fall through to onError.
        if (
          error instanceof ApiError &&
          !error.isNetworkError &&
          error.status !== 409 &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return { created: [], notCreated: phrases, duplicates: [] };
        }
        throw error;
      }

      const failedIdx = new Set((response.failed ?? []).map((f) => f.index));
      const duplicateIdx = new Set((response.duplicates ?? []).map((d) => d.index));
      const createdIdx = new Set((response.created ?? []).map((c) => c.index));
      const createdPhraseIdx = new Set<number>();
      const duplicatePhraseIdx = new Set<number>();
      sentPhraseIndexes.forEach((phraseIdx, requestIdx) => {
        // Nothing failed and nothing was refused: every sent phrase was created
        // (this also covers a backend that sends no `duplicates`).
        if (failedIdx.size === 0 && duplicateIdx.size === 0) {
          createdPhraseIdx.add(phraseIdx);
        } else if (createdIdx.has(requestIdx)) {
          createdPhraseIdx.add(phraseIdx);
        } else if (duplicateIdx.has(requestIdx)) {
          duplicatePhraseIdx.add(phraseIdx);
        }
      });

      return {
        created: phrases.filter((_, i) => createdPhraseIdx.has(i)),
        duplicates: phrases.filter((_, i) => duplicatePhraseIdx.has(i)),
        notCreated: phrases.filter((_, i) => !createdPhraseIdx.has(i) && !duplicatePhraseIdx.has(i)),
      };
    },
    onSuccess: ({ created, notCreated }, { heroAsin }) => {
      if (created.length >= 1) {
        queryClient.invalidateQueries({ queryKey: ["landscapes"] });
        toast({
          title: created.length === 1 ? "Landscape in progress" : `${created.length} landscapes in progress`,
          description: `${quoteJoin(created)} ${created.length === 1 ? "is" : "are"} being added for ${heroAsin} and will be available shortly.`,
        });
      }

      if (notCreated.length >= 1) {
        const wide = window.matchMedia("(min-width: 640px)").matches;
        sonnerToast.error(
          notCreated.length === 1
            ? "Landscape could not be added"
            : `${notCreated.length} landscapes could not be added`,
          {
            description: `${quoteJoin(notCreated)} could not be added. ${notCreated.length === 1 ? "It is" : "They are"} still in the field \u2014 try again.`,
            ...(wide ? { position: "top-right" as const } : {}),
          },
        );
      }
    },
    onError: (error) => handleApiError(error),
  });

export const useGetLandscapesForBrand = (heroProductIds: number[]) => {
  const results = useQueries({
    queries: heroProductIds.map((id) => ({
      queryKey: ["landscapes", id],
      queryFn: () =>
        apiService
          .get<GetLandscapesResponse, { hero_product_id: number }>("/landscapes", { hero_product_id: id })
          .then((data) => ({ heroProductId: id, landscapes: data?.landscapes ?? [] })),
      enabled: heroProductIds.length > 0,
      placeholderData: keepPreviousData,
      staleTime: DEFAULT_STALE_TIME,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const data = results.flatMap((r) =>
    r.data ? [{ heroProductId: r.data.heroProductId, landscapes: r.data.landscapes }] : [],
  );
  return { data, isLoading };
};

export type ProductOverviewStats = {
  landscape_count: number;
  recommendation_count: number;
};

export const useGetProductOverviewStats = (brandId?: number | null) =>
  useQuery<ProductOverviewStats>({
    queryKey: ["dashboard", "product-overview-stats", brandId],
    queryFn: () =>
      apiService.get<ProductOverviewStats, { brand_id: number }>(
        "/dashboard/product-overview-stats",
        { brand_id: brandId! },
      ),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetRecentLandscapesForDashboard = (
  brandId?: number | null,
  lastDays = 30,
) =>
  useQuery({
    queryKey: ["landscapes", "recent-dashboard", brandId, lastDays],
    queryFn: () =>
      apiService.get<GetRecentLandscapesResponse, { brand_id: number; last_days: number }>(
        "/landscapes/recent",
        { brand_id: brandId!, last_days: lastDays },
      ),
    enabled: !!brandId,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
  });
